import { Fragment, useCallback, useMemo, useState } from "react";
import { Button, Col, Collapse, Row, Table } from "react-bootstrap";

import { useAccumulators } from "../../../contexts/accumulators";
import { useMembers } from "../../../contexts/members";
import { formatCurrency } from "../../../helper-functions";
import { useIsMemberCovered } from "../../../hooks/useIsMemberCovered";
import { useIsValidProvider } from "../../../hooks/useIsValidProvider";
import { AccumulatorNumbers, AccumulatorWithDate, MemberAccumulator } from "../../../models/Accumulator";
import { LoadingSpinner } from "../../loading/Loading";
import { AccumulatorProduct, AccumulatorProductTable } from "./AccumulatorTable";
import { getAccumulatorName, getFullName, getIndividualLimitRemaining } from "./utils";
import { Member } from "../../../models/Member";

export const AccumulatorInformation = () => {
    const { accumulators, lastServiceDates, loading, planAllotments } = useAccumulators();
    const { isMember, getMyPlanDetailPdf, isGettingPdf, member, memberDependents, myInsuranceInfo } = useMembers();

    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const toggleRowExpand = (row: string) => {
        setExpandedRows((prev) => {
            if (prev.includes(row)) {
                return prev.filter((r) => r !== row);
            }
            return [...prev, row];
        });
    };

    const isMemberCoveredDental = useIsMemberCovered("dental");
    const isMemberCoveredVision = useIsMemberCovered("vision");

    const isDentalProvider = useIsValidProvider("D");
    const isVisionProvider = useIsValidProvider("V");

    const showDental = isMemberCoveredDental || isDentalProvider;
    const showVision = isMemberCoveredVision || isVisionProvider;

    const dentalCoverage = useMemo(() => {
        return myInsuranceInfo?.coverageInformation.find((cov) => cov.coverageType === "Dental");
    }, [myInsuranceInfo]);

    const visionCoverage = useMemo(() => {
        return myInsuranceInfo?.coverageInformation.find((cov) => cov.coverageType === "Vision");
    }, [myInsuranceInfo]);

    const planYearAllowance = useMemo(() => {
        return (
            planAllotments.find((allot) => allot.accumulatorNumber === AccumulatorNumbers.PlanYearAllowance)
                ?.benefitValue ?? 0
        );
    }, [planAllotments]);

    // combine member, accumulators, and last service dates
    const memberAccumulators = useMemo((): MemberAccumulator[] => {
        let members: Member[] = [];
        if (isMember && !!myInsuranceInfo) {
            members = myInsuranceInfo.enrollmentInformation.map<Member>((enrollmentInfo) => ({
                ...enrollmentInfo,
                participantIDNumber: enrollmentInfo.participantIdNumber,
                planCode: "",
                accountNumber: 0,
                memberProductLinkTermDate: "",
                memberProductLinkEffectiveDate: "",
                birthDate: enrollmentInfo.birthDate ?? "",
            }));
        }
        if (!isMember && !!member) {
            members = [member, ...memberDependents];
        }
        return members.map((mem) => {
            return {
                ...mem,
                // find the current member's accumulators
                // then map over them to find the last service date for that accumulator
                accumulators: accumulators
                    .filter(
                        (a) =>
                            a.dependentSequenceNumber !== null &&
                            a.dependentSequenceNumber === mem.dependentSequenceNumber
                    )
                    .map<AccumulatorWithDate>((a) => {
                        const date = lastServiceDates.find((d) => {
                            let matchesCodeDescription = d.accumulatorType.trim() === a.codeDescription.trim();
                            if (d.accumulatorType.trim() == "Lens/Contacts") {
                                matchesCodeDescription =
                                    a.accumulatorNumber === AccumulatorNumbers.Lenses ||
                                    a.accumulatorNumber === AccumulatorNumbers.Contacts;
                            }
                            return d.dependentSequenceNumber === mem.dependentSequenceNumber && matchesCodeDescription;
                        });

                        return { ...a, lastServiceDate: date?.lastServiceDate1 ?? "" };
                    }),
            };
        });
    }, [isMember, accumulators, lastServiceDates, myInsuranceInfo, member, memberDependents]);

    const findAllotmentByAccumulator = useCallback(
        (accumulator: AccumulatorNumbers) => {
            return planAllotments.find((allotment) => allotment.accumulatorNumber === accumulator);
        },
        [planAllotments]
    );

    const getDentalProducts = useCallback(
        (accumulators: AccumulatorWithDate[]): AccumulatorProduct[] => {
            return [
                {
                    name: getAccumulatorName(AccumulatorNumbers.PlanYearAllowance),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.PlanYearAllowance),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.PlanYearAllowance),
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.PlanYearDeductible),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.PlanYearDeductible),
                    accumulator: accumulators.find(
                        (a) => a.accumulatorNumber === AccumulatorNumbers.PlanYearDeductible
                    ),
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.DentalExams),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.DentalExams),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.DentalExams),
                    defaultAllotment: 2,
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.Cleanings),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.Cleanings),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.Cleanings),
                    defaultAllotment: 2,
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.XRayBitewing),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.XRayBitewing),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.XRayBitewing),
                    defaultAllotment: 2,
                },
                // {
                //     allotment: findAllotmentByAccumulator(AccumulatorNumbers.XRayPano),
                //     accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.XRayPano),
                //     defaultAllotment: 1,
                //     name: getAccumulatorName(AccumulatorNumbers.XRayPano),
                // },
                {
                    name: getAccumulatorName(AccumulatorNumbers.Fluoride),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.Fluoride),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.Fluoride),
                    defaultAllotment: 2,
                },
            ];
        },
        [findAllotmentByAccumulator]
    );

    const getVisionProducts = useCallback(
        (accumulators: AccumulatorWithDate[]): AccumulatorProduct[] => {
            const frames = accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.Frames);
            const lenses = accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.Lenses);
            const contacts = accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.Contacts);

            const hasContacts = !!contacts;
            const hasGlasses = !!frames || !!lenses;
            const hasNeither = !hasContacts && !hasGlasses;

            return [
                {
                    name: getAccumulatorName(AccumulatorNumbers.VisionExams),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.VisionExams),
                    accumulator: accumulators.find((a) => a.accumulatorNumber === AccumulatorNumbers.VisionExams),
                    defaultAllotment: 2,
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.Frames),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.Frames),
                    accumulator: frames,
                    defaultAllotment: 1,
                    showWarning: hasContacts || hasNeither,
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.Lenses),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.Lenses),
                    accumulator: lenses,
                    defaultAllotment: 1,
                    showWarning: hasContacts || hasNeither,
                },
                {
                    name: getAccumulatorName(AccumulatorNumbers.Contacts),
                    allotment: findAllotmentByAccumulator(AccumulatorNumbers.Contacts),
                    accumulator: contacts,
                    showWarning: hasGlasses || hasNeither,
                },
            ];
        },
        [findAllotmentByAccumulator]
    );

    if (!myInsuranceInfo) {
        return null;
    }

    return (
        <>
            <Row>
                <Col>
                    <h5 className="border border-bottom-0 p-3 mb-0 form-label">Benefit Accumulators Information</h5>
                </Col>
            </Row>
            <Row>
                <Col>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Name</th>
                                    {showDental && (
                                        <>
                                            <th>Individual Limit Remaining</th>
                                            <th>Dental Product Plan</th>
                                            <th>Dental Product Code</th>
                                        </>
                                    )}
                                    {showVision && (
                                        <>
                                            <th>Vision Product Plan</th>
                                            <th>Vision Product Code</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {memberAccumulators.map((ma, i) => {
                                    const maKey = `ma-${ma.dependentSequenceNumber}-${i}`;
                                    const isDependent = ma.dependentSequenceNumber !== 0;
                                    return (
                                        <Fragment key={maKey}>
                                            <tr>
                                                <td>
                                                    <Button size="sm" onClick={() => toggleRowExpand(maKey)}>
                                                        <i
                                                            className={
                                                                expandedRows.includes(maKey)
                                                                    ? `fas fa-minus`
                                                                    : `fas fa-plus`
                                                            }
                                                        />
                                                    </Button>
                                                </td>
                                                <td>{getFullName(ma)}</td>
                                                {showDental && (
                                                    <>
                                                        <td>
                                                            {formatCurrency(
                                                                getIndividualLimitRemaining(
                                                                    planYearAllowance,
                                                                    ma.accumulators
                                                                )
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!isDependent && dentalCoverage && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() =>
                                                                        getMyPlanDetailPdf(dentalCoverage.productPlan)
                                                                    }
                                                                    disabled={isGettingPdf}
                                                                >
                                                                    {dentalCoverage.product}{" "}
                                                                    {dentalCoverage.productPlan}
                                                                </Button>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!isDependent && dentalCoverage && (
                                                                <>{dentalCoverage.productPlan}</>
                                                            )}
                                                        </td>
                                                    </>
                                                )}
                                                {showVision && (
                                                    <>
                                                        <td>
                                                            {!isDependent && visionCoverage && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() =>
                                                                        getMyPlanDetailPdf(visionCoverage.productPlan)
                                                                    }
                                                                    disabled={isGettingPdf}
                                                                >
                                                                    {visionCoverage.product}{" "}
                                                                    {visionCoverage.productPlan}
                                                                </Button>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!isDependent && visionCoverage && (
                                                                <>{visionCoverage.productPlan}</>
                                                            )}
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                            <tr>
                                                <td colSpan={7}>
                                                    <Collapse in={expandedRows.includes(maKey)}>
                                                        <div>
                                                            {showDental && (
                                                                <AccumulatorProductTable
                                                                    type="dental"
                                                                    products={getDentalProducts(
                                                                        ma.accumulators.filter(
                                                                            (a) =>
                                                                                a.accumulatorProduct === "Dental" &&
                                                                                a.dependentSequenceNumber != null
                                                                        )
                                                                    )}
                                                                    dependentSequenceNumber={ma.dependentSequenceNumber}
                                                                />
                                                            )}
                                                            {showVision && (
                                                                <AccumulatorProductTable
                                                                    type="vision"
                                                                    products={getVisionProducts(
                                                                        ma.accumulators.filter(
                                                                            (a) =>
                                                                                a.accumulatorProduct === "Vision" &&
                                                                                a.dependentSequenceNumber != null
                                                                        )
                                                                    )}
                                                                    dependentSequenceNumber={ma.dependentSequenceNumber}
                                                                />
                                                            )}
                                                        </div>
                                                    </Collapse>
                                                </td>
                                            </tr>
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Col>
            </Row>
        </>
    );
};
